import React, { FC } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useConfiguration } from "../../../api/configurations/use-configuration";
import {
  noDecimalNumberFormatter,
  percentFormatter,
  useGraphValueFormatter,
} from "../../../api/formatters/dashboard";
import theme from "../../../theme";
import { StackedBarMetricData } from "../../../types/analytics";

import { GraphConfig } from "./Utils/graphConfigs";
import {
  getBarSize,
  getStackedBarStackId,
  groupColors,
} from "./Utils/graphHelpers";
import {
  CustomizedAxisTick,
  CustomLabel,
  CustomResponsiveContainer,
  StyledLegendText,
} from "./Utils/graphStyleCustoms";

interface BiaxialBarGraphProps {
  config: GraphConfig;
  groupMetricData: StackedBarMetricData[];
}
export const BiaxialBarGraph: FC<BiaxialBarGraphProps> = ({
  config,
  groupMetricData,
}) => {
  const updatedConfig = {
    ...config,
    stackedBarLegendTitles:
      config.stackedBarLegendTitles || groupMetricData[0].ids!,
  };

  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const graphValueFormatter = useGraphValueFormatter();

  const yAxisPosition = ["left", "right"];
  return (
    <CustomResponsiveContainer width="100%" height={300}>
      <BarChart
        width={500}
        height={300}
        data={groupMetricData}
        style={{ marginLeft: -14 }}
        margin={{
          top: 5,
          bottom: 5,
        }}
      >
        <CartesianGrid
          style={{ stroke: theme.palette.disabled.light }}
          vertical={false}
        />
        <XAxis
          interval={0}
          tick={(props: any) => {
            return (
              // @ts-ignore
              <CustomizedAxisTick
                props={props}
                groupMetricData={groupMetricData}
                graphValueFormatter={graphValueFormatter}
                config={updatedConfig}
              />
            );
          }}
          axisLine={{ stroke: theme.palette.disabled.light }}
          tickLine={false}
          dataKey="name"
        />
        {updatedConfig.stackedBarLegendTitles!.map((val, index) => (
          <YAxis
            key={index}
            tickFormatter={(value: any) =>
              graphValueFormatter(true, value, updatedConfig, index)!
            }
            axisLine={false}
            yAxisId={yAxisPosition[index]}
            orientation={yAxisPosition[index] as "left" | "right"}
            tickLine={false}
            style={{ fontFamily: theme.typography.fontFamily, fontSize: 14 }}
          />
        ))}

        <Tooltip
          contentStyle={{
            fontFamily: theme.typography.fontFamily,
            borderRadius: 5,
            fontSize: 14,
            backgroundColor: "rgba(97, 97, 97, 0.92)",
            color: "#FFF",
            border: "1px solid transparent",
          }}
          itemStyle={{ color: "#FFF" }}
          cursor={{ fill: "transparent" }}
          formatter={(value: any, name: any) =>
            name === updatedConfig.stackedBarLegendTitles![0]
              ? noDecimalNumberFormatter(
                  value,
                  false,
                  storeCurrencyConfig!.value!
                )
              : percentFormatter(value, false, storeCurrencyConfig!.value!)
          }
        />
        <Legend
          wrapperStyle={{
            paddingLeft: "25px",
            paddingTop: "10px",
          }}
          align={"center"}
          formatter={(value, entry, index) => {
            return StyledLegendText(value, entry, index);
          }}
          iconType={"circle"}
          iconSize={9}
        />
        {updatedConfig.stackedBarLegendTitles!.map((val, index) => (
          <Bar
            yAxisId={yAxisPosition[index]}
            key={val}
            isAnimationActive={false}
            name={val}
            dataKey={val}
            stackId={getStackedBarStackId(index, updatedConfig)}
            fill={groupColors[index]}
            radius={[10, 10, 0, 0]}
            barSize={getBarSize(false, groupMetricData.length)}
          >
            <LabelList
              content={(props: any) => {
                return (
                  // @ts-ignore
                  <CustomLabel
                    props={props}
                    config={updatedConfig}
                    graphValueFormatter={graphValueFormatter}
                    compareGroupA={undefined}
                    compareGroupB={undefined}
                    index={index}
                  />
                );
              }}
            />
          </Bar>
        ))}
      </BarChart>
    </CustomResponsiveContainer>
  );
};
